import type { Survey } from '@engagement-web-app/common';
import { uuidSchema } from '@engagement-web-app/common/validation-utils';
import { useQuery } from '@tanstack/react-query';
import {
    createFileRoute,
    type ErrorComponentProps,
    SearchParamError,
    stripSearchParams,
    useLocation,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { ListIcon } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Form } from '@/components/Form';
import { InvalidRequest } from '@/components/InvalidRequest';
import { Pill } from '@/components/Pill';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { type Tab, Tabs, TabsSkeleton } from '@/components/Tabs';
import { fetchOrganization } from '@/queries/organizations';
import { fetchSurveyMappings, fetchSurveys, fetchSurveysAttributes } from '@/queries/surveys/system';
import { combineQueryErrors } from '@/utils';
import { AttributeList, type AttributeMappingFormData, AttributeMappingList } from './-fragments/attributes';
import {
    CategoryList,
    type CategoryMappingFormData,
    CategoryMappingList,
    CategoryMappingListSkeleton,
} from './-fragments/categories';
import { OptionList, type OptionMappingFormData, OptionMappingPanel } from './-fragments/options';
import { QuestionList, type QuestionMappingFormData, QuestionMappingPanel } from './-fragments/questions';
import {
    useSaveAttributeOptionMappings,
    useSaveAttributeSettingMappings,
    useSaveCategoryMappings,
    useSaveQuestionMappings,
} from './-hooks';
import * as styles from './index.css';

const DefaultRouteQueryParams = {
    surveyId: '',
};

const RouteQueryParams = z.object({
    surveyId: uuidSchema.default(DefaultRouteQueryParams.surveyId),
});

export const Route = createFileRoute('/system/_auth/organizations/$organizationId/mapping/')({
    component: SystemShowOrganizationSurveyMappingsRoute,
    loader: async ({ params }) => {
        const [organization, surveys] = await Promise.all([
            fetchOrganization({ uuid: params.organizationId }),
            fetchSurveys(params.organizationId),
        ]);
        return { organization, surveys: surveys.surveys };
    },
    validateSearch: zodValidator(RouteQueryParams),
    search: {
        middlewares: [stripSearchParams(DefaultRouteQueryParams)],
    },
    pendingComponent: SystemShowOrganizationSurveyMappingsRouteSkeleton,
    errorComponent: SystemShowOrganizationSurveyMappingsRouteError,
});

function SystemShowOrganizationSurveyMappingsRouteError({ error, reset }: ErrorComponentProps) {
    // `validateSearch` throwing (e.g. a malformed `surveyId` failing `schema.parse()`) gets
    // caught by the router and re-thrown as its own `SearchParamError`, distinct from the
    // loader failing, which calls for a retry rather than navigating away from a URL that
    // was never wrong to begin with.
    if (error instanceof SearchParamError) {
        return <InvalidRequest kind="system" />;
    }

    return <ErrorOverlay active onRetry={reset} />;
}

function SystemShowOrganizationSurveyMappingsRoute() {
    const navigate = Route.useNavigate();
    const location = useLocation();
    const { surveys } = Route.useLoaderData();
    const search = Route.useSearch();

    if (!surveys.length) {
        // TODO: Visual design for empty state
        return <div>サーベイがありません。</div>;
    }

    // TODO: A missing/stale `surveyId` (bad bookmark, deleted survey) silently falls back
    // to the first survey rather than redirecting to a canonical URL or showing a
    // not-found state. Revisit if this page gets wider use.
    const selectedSurvey = surveys.find((s) => s.surveyId === search.surveyId) ?? surveys[0]!;
    const selectedSurveyIndex = surveys.findIndex((s) => s.surveyId === selectedSurvey.surveyId);
    const previousSurvey = surveys.at(selectedSurveyIndex + 1);

    const handleSurveyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        navigate({ to: location.pathname, search: { ...search, surveyId: e.target.value } });
    };

    return (
        <>
            <Section>
                <SectionHeader
                    title="サーベイ選択"
                    description="設定対象のサーベイを選択します。このページの設定は、ここで選んだサーベイにのみ適用されます。"
                />

                <Flex justify="start">
                    <Select value={selectedSurvey.surveyId} onChange={handleSurveyChange}>
                        {surveys.map((s) => (
                            <option key={s.surveyId} value={s.surveyId}>
                                {s.surveyName}
                            </option>
                        ))}
                    </Select>
                </Flex>
            </Section>

            <SurveyMappings currentSurvey={selectedSurvey} previousSurvey={previousSurvey ?? null} />
        </>
    );
}

type SurveyMappingsProps = {
    currentSurvey: Survey;
    previousSurvey: Survey | null;
};

type TabId = 'categories' | 'questions' | 'attributes' | 'options';

function SurveyMappings({ currentSurvey, previousSurvey }: SurveyMappingsProps) {
    const { organization } = Route.useLoaderData();
    const [tab, setTab] = useState<TabId>('categories');

    const currentSurveyId = currentSurvey.surveyId;

    const saveCategoryMappingsMutation = useSaveCategoryMappings(organization.uuid, currentSurveyId);
    const saveQuestionMappingsMutation = useSaveQuestionMappings(organization.uuid, currentSurveyId);
    const saveAttributeSettingMappingsMutation = useSaveAttributeSettingMappings(organization.uuid, currentSurveyId);
    const saveAttributeOptionMappingsMutation = useSaveAttributeOptionMappings(organization.uuid, currentSurveyId);

    const surveyMappingsQuery = useQuery({
        queryKey: ['surveyMappings', organization.uuid, currentSurveyId],
        queryFn: () => fetchSurveyMappings(organization.uuid, currentSurveyId),
    });

    const surveyIdsToFetch = previousSurvey ? [currentSurveyId, previousSurvey.surveyId] : [currentSurveyId];

    const surveysAttributesQuery = useQuery({
        queryKey: ['surveysAttributes', organization.uuid, surveyIdsToFetch],
        queryFn: () => fetchSurveysAttributes(organization.uuid, surveyIdsToFetch),
    });

    const attributesBySurveyId = new Map(surveysAttributesQuery.data?.map((s) => [s.surveyId, s.attributes]) ?? []);
    const currentAttributes = attributesBySurveyId.get(currentSurveyId) ?? [];
    const previousAttributes = previousSurvey ? (attributesBySurveyId.get(previousSurvey.surveyId) ?? []) : [];

    const categoryMappings = surveyMappingsQuery.data?.categoryMappings ?? [];
    const questionMappings = surveyMappingsQuery.data?.questionMappings ?? [];
    const attributeSettingMappings = surveyMappingsQuery.data?.attributeSettingMappings ?? [];
    const attributeOptionMappings = surveyMappingsQuery.data?.attributeOptionMappings ?? [];
    const totalQuestions = currentSurvey?.categories.reduce((sum, c) => sum + c.questions.length, 0) ?? 0;
    const totalOptions = currentAttributes.reduce((sum, a) => sum + a.options.length, 0);

    // The mutations already show a toast and log on failure (see -hooks), so the
    // rejection is swallowed here rather than left to bubble into RHF's handleSubmit,
    // which would otherwise surface as an unhandled promise rejection.
    const handleSaveCategoryMappings = async (previousSurveyId: string, formData: CategoryMappingFormData) => {
        await saveCategoryMappingsMutation.mutateAsync({ previousSurveyId, formData }).catch(() => {});
    };

    const handleSaveQuestionMappings = async (previousSurveyId: string, formData: QuestionMappingFormData) => {
        await saveQuestionMappingsMutation.mutateAsync({ previousSurveyId, formData }).catch(() => {});
    };

    const handleSaveAttributeSettingMappings = async (previousSurveyId: string, formData: AttributeMappingFormData) => {
        await saveAttributeSettingMappingsMutation.mutateAsync({ previousSurveyId, formData }).catch(() => {});
    };

    const handleSaveAttributeOptionMappings = async (previousSurveyId: string, formData: OptionMappingFormData) => {
        await saveAttributeOptionMappingsMutation.mutateAsync({ previousSurveyId, formData }).catch(() => {});
    };

    const tabs: Tab<TabId>[] = [
        { id: 'categories', label: `1. カテゴリー (${currentSurvey?.categories.length ?? 0})`, icon: ListIcon },
        { id: 'questions', label: `2. 設問 (${totalQuestions})`, icon: ListIcon },
        { id: 'attributes', label: `3. 属性 (${currentAttributes.length})`, icon: ListIcon },
        { id: 'options', label: `4. 属性選択肢 (${totalOptions})`, icon: ListIcon },
    ];

    const { isError, retry } = combineQueryErrors(surveyMappingsQuery, surveysAttributesQuery);

    if (isError) {
        return <ErrorOverlay active={isError} onRetry={retry} message="サーベイマッピングの取得に失敗しました。" />;
    }

    if (surveyMappingsQuery.isLoading || surveysAttributesQuery.isLoading) {
        return (
            <Section pt="8">
                {/* TODO: For now let's just show the category tab skeleton for all tabs as a shortcut */}
                <CategoryMappingListSkeleton />
            </Section>
        );
    }

    return (
        <Section pt="8">
            <Pill variant={currentSurvey.isPublished ? 'secondary' : 'tertiary'} className={styles.statusPill}>
                {currentSurvey.isPublished ? '公開中' : '未公開'}
            </Pill>

            <SectionHeader title={organization.name} description={`サーベイ：${currentSurvey.surveyName}`} />

            <Flex direction="column" gap="6">
                <Flex justify="start">
                    <Tabs tabs={tabs} activeTabId={tab} onTabChange={setTab} />
                </Flex>

                {previousSurvey ? (
                    <>
                        {tab === 'categories' && (
                            <CategoryMappingList
                                organizationId={organization.uuid}
                                previousSurvey={previousSurvey}
                                currentSurvey={currentSurvey}
                                mappings={categoryMappings}
                                onSaveMappings={handleSaveCategoryMappings}
                            />
                        )}
                        {tab === 'questions' && (
                            <QuestionMappingPanel
                                currentSurvey={currentSurvey}
                                previousSurvey={previousSurvey}
                                categoryMappings={categoryMappings}
                                questionMappings={questionMappings}
                                onSaveMappings={handleSaveQuestionMappings}
                            />
                        )}
                        {tab === 'attributes' && (
                            <AttributeMappingList
                                currentSurveyId={currentSurveyId}
                                previousSurveyId={previousSurvey.surveyId}
                                currentAttributes={currentAttributes}
                                previousAttributes={previousAttributes}
                                mappings={attributeSettingMappings}
                                onSaveMappings={handleSaveAttributeSettingMappings}
                            />
                        )}
                        {tab === 'options' && (
                            <OptionMappingPanel
                                currentAttributes={currentAttributes}
                                previousAttributes={previousAttributes}
                                attributeSettingMappings={attributeSettingMappings}
                                optionMappings={attributeOptionMappings}
                                onSaveMappings={handleSaveAttributeOptionMappings}
                                previousSurveyId={previousSurvey.surveyId}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {tab === 'categories' && <CategoryList survey={currentSurvey} />}
                        {tab === 'questions' && <QuestionList survey={currentSurvey} />}
                        {tab === 'attributes' && <AttributeList attributes={currentAttributes} />}
                        {tab === 'options' && <OptionList attributes={currentAttributes} />}
                    </>
                )}
            </Flex>
        </Section>
    );
}

function SystemShowOrganizationSurveyMappingsRouteSkeleton() {
    return (
        <>
            <Section>
                {/* The title/description are static, so they render for real rather than as placeholders. */}
                <SectionHeader
                    title="サーベイ選択"
                    description="設定対象のサーベイを選択します。このページの設定は、ここで選んだサーベイにのみ適用されます。"
                />

                <Flex justify="start">
                    <Form.FieldSkeleton label={false} />
                </Flex>
            </Section>

            <Section pt="8">
                <Skeleton.Box width={64} height={24} />

                <SectionHeader
                    title={<Skeleton.Line width={160} height="xl" />}
                    description={<Skeleton.Line width={220} height="sm" />}
                />

                <Flex direction="column" gap="6">
                    <Flex justify="start">
                        <TabsSkeleton />
                    </Flex>

                    {/* Using the same skeleton to represent all four tabs here too, as a shortcut */}
                    <CategoryMappingListSkeleton />
                </Flex>
            </Section>
        </>
    );
}
